import { Component, OnDestroy, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import {
  AddonBin,
  AddonBinStationStatusEnum,
  AddonBinStatusEnum,
  ItemDetailAddon,
} from "app/model/entities/entity-model";
import { AddonUtilsService } from "app/shared/addon-utils.service";
import { DomainBaseComponent, wrapBusy } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { ScannerInputComponent } from "app/shared/scanner-input.component";
import { UnitOfWork } from "app/shared/unit-of-work";
import { UtilFns } from "app/shared/util-fns";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-handheld-bin-process",
  templateUrl: "./handheld-bin-process.component.html",
  standalone: false,
})
export class HandheldBinProcessComponent extends DomainBaseComponent implements OnDestroy {
  @ViewChild(ScannerInputComponent) scannerInput: ScannerInputComponent<ItemDetailAddon[]>;

  addonBin: AddonBin;
  itemDetailAddons: ItemDetailAddon[] = [];
  showItemList = false;
  lastScannedItem: ItemDetailAddon | null = null;

  AddonBinStationStatusEnum = AddonBinStationStatusEnum;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected addonUtilsService: AddonUtilsService,
    public override uow: UnitOfWork
  ) {
    super(domainService);

    this.route.paramMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  async updateFromContext() {
    const addonBinId = +this.route.snapshot.params["addonBinId"];
    this.addonBin = await this.dbQueryService.getAddonBin(addonBinId);
    await this.refreshData();
    this.isPageReady = true;

    await UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  async refreshData() {
    this.itemDetailAddons = await this.dbQueryService.getItemDetailAddonsForAddonBin(this.addonBin.id);
  }

  override ngOnDestroy() {
    this.uow.clearEntities(AddonBin);
    this.uow.clearEntities(ItemDetailAddon);
    super.ngOnDestroy();
  }

  // --- Scan logic (ported from addon-bin-process.component.ts) ---

  searchItemDetailForScan = async (scannedValue: string): Promise<ItemDetailAddon[] | null> => {
    const itdas = this.addonUtilsService.findScannableAddons(this.addonBin, this.itemDetailAddons, scannedValue);
    return itdas.length === 0 ? null : itdas;
  };

  /** Chain of scans and station changes; each one starts only after the previous one has saved. */
  private pendingWork: Promise<void> = Promise.resolve();

  /**
   * A wedge scanner sends the next scan while the previous save is still running. Breeze rejects a save that
   * overlaps another one ("Concurrent saves not allowed"), and a save result overwrites edits made to its
   * entities while it ran, so every edit-and-save on this page goes through this queue.
   */
  private enqueue(op: () => Promise<void>): Promise<void> {
    const run = this.pendingWork.then(op);
    this.pendingWork = run.catch(() => undefined);
    return run;
  }

  onScanSuccess(itdas: ItemDetailAddon[]) {
    if (!itdas || itdas.length === 0) return;
    return this.enqueueScan(itdas[0].itemDetailId);
  }

  markAsScanned(itda: ItemDetailAddon) {
    return this.enqueueScan(itda.itemDetailId);
  }

  private enqueueScan(itemDetailId: string) {
    return this.enqueue(() => this.processScan(itemDetailId));
  }

  @wrapBusy
  private async processScan(itemDetailId: string) {
    // Find the rows again: a scan that ran earlier in the queue can move the bin to its next station.
    const itdas = this.addonUtilsService.findScannableAddons(this.addonBin, this.itemDetailAddons, itemDetailId);
    if (itdas.length === 0) {
      this.toastr.warning(`The item ${itemDetailId} is not at the pending station. Scan a different item.`);
      await UtilFns.wait(1);
      this.scannerInput?.focus();
      return;
    }
    await this.applyScan(itdas);
    this.lastScannedItem = itdas[0];
    this.toastr.success("The system scanned the item.");
  }

  private async applyScan(itdas: ItemDetailAddon[]) {
    this.addonUtilsService.applyScanIntoPendingStation(this.addonBin, this.itemDetailAddons, itdas);
    await this.dbSaveService.saveChanges();
    await UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  onScanError(message: string) {
    // Scanner component shows error; just refocus
  }

  scanAll() {
    return this.enqueue(() => this.scanAllCore());
  }

  @wrapBusy
  private async scanAllCore() {
    const itdas = this.itemDetailAddons.filter((x) => this.addonUtilsService.isAddonRowScannable(x, this.addonBin));
    if (itdas.length === 0) {
      await this.dialogService.showOkMessage("No Items to Scan", "The items in this bin are complete at this station.");
      return;
    }
    await this.applyScan(itdas);
    this.toastr.success(`The system scanned ${new Set(itdas.map((x) => x.itemDetailId)).size} items.`);
  }

  // --- Station management (ported from addon-bin-process.component.ts) ---

  canScanIntoStation(): boolean {
    return this.addonBin?.pendingAddonStationId != null;
  }

  canMarkStationInProcess(): boolean {
    return (
      this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.Await ||
      this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.OnHold
    );
  }

  canMarkStationComplete(): boolean {
    return this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.InProcess;
  }

  canMarkStationOnHold(): boolean {
    return (
      this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.Await ||
      this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.InProcess
    );
  }

  canScanAll(): boolean {
    return this.authUser?.isShippingAdmin && this.addonBin?.currentAddonBinStationStatusId == null;
  }

  markStation(stationStatusCode: AddonBinStationStatusEnum) {
    return this.enqueue(() => this.markStationCore(stationStatusCode));
  }

  @wrapBusy
  private async markStationCore(stationStatusCode: AddonBinStationStatusEnum) {
    this.addonBin.currentAddonBinStationStatusId = stationStatusCode;
    const itdas = this.itemDetailAddons.filter(
      (x) => x.addon.addonStationId === this.addonBin.currentAddonStationId
    );
    itdas.forEach((x) => (x.addonBinStationStatusId = stationStatusCode));

    if (stationStatusCode === AddonBinStationStatusEnum.Closed) {
      this.addonUtilsService.updatePendingAddonStation(this.addonBin);
    } else if (stationStatusCode === AddonBinStationStatusEnum.InProcess) {
      this.addonBin.addonBinStatusId = AddonBinStatusEnum.InProcess;
    } else if (stationStatusCode === AddonBinStationStatusEnum.OnHold) {
      this.addonBin.addonBinStatusId = AddonBinStatusEnum.OnHold;
    }

    await this.dbSaveService.saveChanges();
    this.toastr.success(`Station marked as ${this.getStationStatusLabel(stationStatusCode)}`);

    // Refocus scanner if still in scanning mode
    await UtilFns.wait(1);
    this.scannerInput?.focus();
  }

  isBinComplete(): boolean {
    return (
      this.addonBin?.currentAddonBinStationStatusId === AddonBinStationStatusEnum.Closed &&
      this.addonBin?.pendingAddonStationId == null
    );
  }

  // --- Progress helpers ---

  get pendingStationItems(): ItemDetailAddon[] {
    if (!this.addonBin?.pendingAddonStationId) return [];
    return this.itemDetailAddons.filter(
      (x) => x.addon.addonStationId === this.addonBin.pendingAddonStationId
    );
  }

  get scannedCount(): number {
    return this.pendingStationItems.filter(
      (x) => x.addonBinStationStatusId === AddonBinStationStatusEnum.Await
    ).length;
  }

  get totalCount(): number {
    return this.pendingStationItems.length;
  }

  get progressPercent(): number {
    return this.totalCount > 0 ? (this.scannedCount / this.totalCount) * 100 : 0;
  }

  isItemScanned(itda: ItemDetailAddon): boolean {
    return itda.addonBinStationStatusId === AddonBinStationStatusEnum.Await;
  }

  canManuallyMarkScanned(itda: ItemDetailAddon): boolean {
    return (
      this.user?.isBinProcessAdmin &&
      this.addonUtilsService.isAddonRowScannable(itda, this.addonBin) &&
      itda.addonBinStationStatusId !== AddonBinStationStatusEnum.Await
    );
  }

  getStationStatusLabel(code: AddonBinStationStatusEnum): string {
    switch (code) {
      case AddonBinStationStatusEnum.InProcess: return "In Process";
      case AddonBinStationStatusEnum.Closed: return "Complete";
      case AddonBinStationStatusEnum.OnHold: return "On Hold";
      default: return "";
    }
  }

  goBack() {
    this.router.navigate(["/handheld/process-bins"]);
  }
}
